/**
 * Volume preferences persisted in localStorage so they survive page reloads
 * and follow the player across sessions on the same device. We don't sync to
 * the server because audio output volume is intrinsically a per-device thing.
 *
 * There is no separate "mute" flag: dragging the master slider to 0 is the
 * mute. The sliders live in the global top nav (always visible, revealed on
 * hover over the speaker icon) and drive every audio surface through the
 * `subscribeVolumeChanges` pub/sub below. The legacy `muted: true` value in
 * older localStorage payloads is migrated to `master: 0` on load — same
 * audible result, no UI surface for re-toggling.
 *
 * Why a custom event on top of localStorage: the `storage` event only fires
 * in *other* tabs, so we need an in-tab dispatch to keep the active game
 * session's audio engine in sync with the TopNav slider. `subscribeVolumeChanges`
 * unifies both — same-tab CustomEvent and cross-tab StorageEvent.
 */

export interface VolumePrefs {
  master: number;
  music: number;
  sfx: number;
}

const STORAGE_KEY = 'gd-volume-prefs';
const VOLUME_CHANGE_EVENT = 'gd:volume-changed';

export const DEFAULT_VOLUMES: VolumePrefs = {
  master: 1.0,
  music: 0.8,
  sfx: 0.9,
};

export function clampVolume(v: number): number {
  if (!Number.isFinite(v)) {
    return 0;
  }
  return Math.max(0, Math.min(1, v));
}

/**
 * Coerce a possibly-string-or-other value into a number, falling back
 * to `fallback` if it can't be parsed cleanly. Lets hand-edited or
 * legacy localStorage entries (`"0.7"` rather than `0.7`) round-trip
 * without losing the saved level.
 */
function coerceNumber(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string') {
    const n = Number(v);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return fallback;
}

export function loadVolumePrefs(): VolumePrefs {
  if (typeof localStorage === 'undefined') {
    return { ...DEFAULT_VOLUMES };
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { ...DEFAULT_VOLUMES };
    }
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const wasMuted = parsed.muted === true || parsed.muted === 'true' || parsed.muted === 1;
    const master = wasMuted ? 0 : clampVolume(coerceNumber(parsed.master, DEFAULT_VOLUMES.master));
    return {
      master,
      music: clampVolume(coerceNumber(parsed.music, DEFAULT_VOLUMES.music)),
      sfx: clampVolume(coerceNumber(parsed.sfx, DEFAULT_VOLUMES.sfx)),
    };
  } catch {
    return { ...DEFAULT_VOLUMES };
  }
}

export function saveVolumePrefs(prefs: VolumePrefs): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  const safe: VolumePrefs = {
    master: clampVolume(prefs.master),
    music: clampVolume(prefs.music),
    sfx: clampVolume(prefs.sfx),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
  } catch (err) {
    console.warn('Failed to persist volume prefs:', err);
  }
}

/**
 * Persist new volume prefs AND notify all in-tab subscribers. Use this from
 * any UI surface that lets the player change volume — the TopNav slider, a
 * future hotkey, debug tooling — so the running game session can update its
 * audio engine immediately without waiting for the next session start.
 *
 * Clamping happens inside `saveVolumePrefs`; the dispatched event carries
 * the same clamped shape so subscribers see exactly what was persisted.
 */
export function publishVolumeChange(prefs: VolumePrefs): void {
  const safe: VolumePrefs = {
    master: clampVolume(prefs.master),
    music: clampVolume(prefs.music),
    sfx: clampVolume(prefs.sfx),
  };
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
    } catch (err) {
      console.warn('Failed to persist volume prefs:', err);
    }
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<VolumePrefs>(VOLUME_CHANGE_EVENT, { detail: safe }));
  }
}

/**
 * Subscribe to volume changes. Fires for both same-tab updates (via
 * `publishVolumeChange`) and cross-tab updates (via the browser's `storage`
 * event when another tab rewrites the prefs key). Returns an unsubscribe
 * function — wire it into a `useEffect` cleanup or class teardown.
 */
export function subscribeVolumeChanges(cb: (prefs: VolumePrefs) => void): () => void {
  if (typeof window === 'undefined') {
    return () => {
      // No-op for non-browser environments (SSR / tests without window).
    };
  }
  const onCustom = (e: Event) => {
    const detail = (e as CustomEvent<VolumePrefs>).detail;
    if (detail) {
      cb(detail);
    }
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) {
      cb(loadVolumePrefs());
    }
  };
  window.addEventListener(VOLUME_CHANGE_EVENT, onCustom);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(VOLUME_CHANGE_EVENT, onCustom);
    window.removeEventListener('storage', onStorage);
  };
}
